/**
 * External dependencies
 */
import { STORY_ANIMATION_STATE } from '@web-stories-wp/animation';

/**
 * Internal dependencies
 */
import type {
  Element,
  TextBoxAttachmentAlignment,
  TextBoxAttachmentPosition,
} from '../../../../elements/types';
import {
  deriveTextBoxAttachment,
  getTextBoxAttachmentValidationError,
  getTextBoxAttachmentPosition,
  isTextBoxAttachment,
  isTextBoxAttachmentDependent,
  resolveTextBoxAttachment,
  stripTextBoxAttachment,
} from '../../../../utils/textBoxAttachment';
import type { ReducerState } from './types';

type SetTextBoxAttachmentProps = {
  elementId: string;
  targetId: string | null;
  alignment?: TextBoxAttachmentAlignment;
  gap?: number;
  position?: TextBoxAttachmentPosition;
};

const PLAYING_STATES = [
  STORY_ANIMATION_STATE.PLAYING,
  STORY_ANIMATION_STATE.PLAYING_SELECTED,
  STORY_ANIMATION_STATE.SCRUBBING,
];

function setTextBoxAttachment(
  state: ReducerState,
  { elementId, targetId, alignment, gap, position }: SetTextBoxAttachmentProps
): ReducerState {
  if (PLAYING_STATES.includes(state.animationState)) {
    return state;
  }

  const pageIndex = state.pages.findIndex(({ id }) => id === state.current);
  const page = state.pages[pageIndex];
  if (!page || page.readonly) {
    return state;
  }

  const elementIndex = page.elements.findIndex(({ id }) => id === elementId);
  const dependent = page.elements[elementIndex];
  if (!isTextBoxAttachmentDependent(dependent)) {
    return state;
  }

  let nextDependent: Element;
  if (targetId === null) {
    if (!dependent.textBoxAttachment) {
      return state;
    }
    nextDependent = stripTextBoxAttachment(dependent);
  } else {
    if (
      getTextBoxAttachmentValidationError(page.elements, dependent.id, targetId)
    ) {
      return state;
    }
    const target = page.elements.find(({ id }) => id === targetId);
    if (!target) {
      return state;
    }

    const current = isTextBoxAttachment(dependent.textBoxAttachment)
      ? dependent.textBoxAttachment
      : null;
    const hasExplicitAlignment = typeof alignment !== 'undefined';
    const nextAlignment =
      alignment ||
      (current?.targetId === targetId ? current.alignment : undefined);
    const currentPosition = getTextBoxAttachmentPosition(current);
    const nextPosition = position || currentPosition;
    const attachment =
      current?.targetId === targetId && currentPosition === nextPosition
        ? { ...current, alignment: nextAlignment || current.alignment }
        : deriveTextBoxAttachment(
            dependent,
            target,
            nextAlignment,
            nextPosition
          );

    if (hasExplicitAlignment) {
      attachment.crossAxisOffset = 0;
    }
    if (Number.isFinite(gap)) {
      attachment.gap = gap as number;
      if (attachment.gap < 0 && attachment.version !== 3) {
        attachment.version = 2;
      }
    }

    nextDependent = resolveTextBoxAttachment(
      { ...dependent, textBoxAttachment: attachment },
      target,
      attachment
    );
  }

  const elements = [...page.elements];
  elements[elementIndex] = nextDependent;
  const pages = [...state.pages];
  pages[pageIndex] = { ...page, elements };
  return { ...state, pages };
}

export default setTextBoxAttachment;
